<template>
  <div class="min-h-screen bg-gray-100">
    <div class="mx-auto p-6">
      <div class="flex justify-between items-start mb-6 mdmax:flex-col gap-4">
        <div>
          <button
            @click="$router.push('/invoices/invoices')"
            class="text-sm text-neutral-600 hover:text-neutral-800 mb-2"
          >
            ← Kembali
          </button>
          <h1 class="text-xl font-bold text-neutral-800">Invoice Detail</h1>
          <div class="text-sm text-neutral-500 mt-1">
            <p>{{ invoice?.invoice?.invoice_number || "-" }}</p>
            <p>{{ formatDate(invoice?.invoice?.invoice_date) }}</p>
          </div>
        </div>

        <div class="flex gap-2">
          <button
            @click="downloadPdf"
            class="px-4 py-2 rounded-lg bg-primary-500 text-white hover:bg-primary-600 font-medium"
          >
            Download PDF
          </button>
        </div>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div class="bg-white rounded-lg p-4 shadow-sm">
          <p class="text-sm text-neutral-500">Customer</p>
          <p class="text-neutral-800 font-bold">
            {{ customerInfo.customer_name || "-" }}
          </p>
          <p class="text-neutral-500 text-sm">
            {{ customerInfo.customer_address || "-" }}
          </p>
        </div>

        <div class="bg-white rounded-lg p-4 shadow-sm">
          <p class="text-sm text-neutral-500 mb-2">Totals</p>
          <div class="flex justify-between items-center">
            <span class="text-sm text-neutral-500">Subtotal</span>
            <span class="font-bold text-neutral-800">{{
              formatCurrency(invoice?.totals?.subtotal || invoice?.subtotal || invoice?.sub_total)
            }}</span>
          </div>
          <div class="flex justify-between items-center mt-1">
            <span class="text-sm text-neutral-500"
              >Discount ({{ invoice?.invoice?.discount_percentage || customerInfo.discount_percent || "0" }}%)</span
            >
            <span class="font-bold text-neutral-800">{{
              formatCurrency(invoice?.totals?.discount_amount || invoice?.discount_amount)
            }}</span>
          </div>
          <div class="flex justify-between items-center mt-1">
            <span class="text-sm text-neutral-500"
              >VAT ({{ invoice?.invoice?.vat_percent || "-" }}%)</span
            >
            <span class="font-bold text-neutral-800">{{
              formatCurrency(invoice?.totals?.vat_amount || invoice?.vat_amount || invoice?.vat_value)
            }}</span>
          </div>
          <div class="flex justify-between items-center mt-2">
            <span class="text-sm text-neutral-500">Total</span>
            <span class="font-bold text-neutral-800">{{
              formatCurrency(
                invoice?.totals?.grand_total || invoice?.total_amount || invoice?.total_price || invoice?.total
              )
            }}</span>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg p-4 shadow-sm mb-6">
        <h2 class="text-lg font-bold text-neutral-800 mb-4">Resi Summary</h2>
        <TableComponent :columns="resiColumns" :list="resiTableData" compact>
          <template #table-row="{ entry, column }">
            <span v-if="column.field === 'shipment_date'">
              {{ formatDate(entry.shipment_date) }}
            </span>
            <span v-else-if="column.field === 'total_price'">
              {{ formatCurrency(entry.total_price) }}
            </span>
            <span v-else>{{ entry[column.field] || "-" }}</span>
          </template>
        </TableComponent>
      </div>

      <div class="bg-white rounded-lg p-4 shadow-sm">
        <h2 class="text-lg font-bold text-neutral-800 mb-4">
          Payment Terms
        </h2>
        <p class="text-neutral-700 whitespace-pre-line">{{ customerInfo.payment_terms || "-" }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api"
import { mapActions } from "vuex"
import TableComponent from "@/components/Table/Table"

export default {
  name: "InvoicesInvoiceDetail",
  components: {
    TableComponent
  },
  data() {
    return {
      invoice: null,
      resiColumns: [
        { label: "Resi Number", field: "resi_number" },
        { label: "Date", field: "shipment_date" },
        { label: "Destination", field: "destination" },
        { label: "Total Price", field: "total_price" }
      ]
    }
  },
  computed: {
    invoiceId() {
      return this.$route.params.id
    },
    customerInfo() {
      const inv = this.invoice || {}
      return (
        inv.customer ||
        inv.invoice_customer ||
        inv.invoiceCustomer ||
        inv.customer_info ||
        {}
      )
    },
    resiItems() {
      const inv = this.invoice || {}
      const candidates = [
        inv.resi,
        inv.resis,
        inv.resi_list,
        inv.resi_items,
        inv.resi_summary,
        inv.items
      ]
      const found = candidates.find((c) => Array.isArray(c))
      return found || []
    },
    resiTableData() {
      return this.resiItems
    },

  },
  methods: {
    ...mapActions("common", ["showLoading", "hideLoading", "setToast"]),
    toastSuccess(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "success",
          duration: 2500,
          primaryText: message,
          secondaryText
        }
      })
    },
    toastError(message, secondaryText = "") {
      this.setToast({
        options: {
          type: "danger",
          duration: 3500,
          primaryText: message,
          secondaryText
        }
      })
    },
    getErrorMessage(err, fallback = "Terjadi kesalahan") {
      return (
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        fallback
      )
    },
    toDecimalString(value, fallback = "0") {
      const raw = String(value ?? "").trim()
      if (!raw) return fallback
      const normalized = raw.replace(/,/g, ".").replace(/[^0-9.-]/g, "")
      return normalized || fallback
    },
    formatCurrency(value) {
      const numberValue = Number(this.toDecimalString(value, "0"))
      if (Number.isNaN(numberValue)) return "Rp 0"
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        maximumFractionDigits: 0
      }).format(numberValue)
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return new Intl.DateTimeFormat("id-ID", {
        day: "numeric",
        month: "long",
        year: "numeric"
      }).format(date);
    },
    async fetchInvoiceDetail() {
      this.showLoading()
      try {
        const res = await api.invoiceGenerator.invoiceDetail(this.invoiceId)
        this.invoice = res?.data?.data || null
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal memuat invoice"))
      } finally {
        this.hideLoading()
      }
    },
    async downloadPdf() {
      this.showLoading()
      try {
        const res = await api.invoiceGenerator.invoicePdfBlob(this.invoiceId)
        const blob = new Blob([res.data], { type: "application/pdf" })
        const url = window.URL.createObjectURL(blob)

        const invoiceNumber =
          this.invoice?.invoice?.invoice_number || `invoice-${this.invoiceId}`
        const filename = invoiceNumber.toLowerCase().endsWith(".pdf")
          ? invoiceNumber
          : `${invoiceNumber}.pdf`

        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", filename)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        this.toastError(this.getErrorMessage(err, "Gagal download PDF"))
      } finally {
        this.hideLoading()
      }
    }
  },
  mounted() {
    this.fetchInvoiceDetail()
  }
}
</script>
